"use client";

import React, { useState } from "react";
import { Mail, Clock, MapPin, Paperclip, Loader, ArrowRight } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function SupportPortal() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section data-section-id="20171" id="secure-inquiry-hub" ref={ref} className={`dark bg-background text-foreground py-24 px-4 md:px-8 relative overflow-hidden transition-opacity duration-700 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
        {}
        <div className="flex flex-col justify-between space-y-12">
          <div className="space-y-8">
            <h2 className="text-4xl md:text-6xl font-default font-bold tracking-tighter uppercase leading-none">
              Secure
              <br />
              <span className="text-muted-foreground">Inquiry</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-md leading-relaxed font-default">
              Our protocols ensure your privacy. Reach out for technical assistance, 
              connectivity support, or crypto-payment inquiries.
            </p>
          </div>

          <div className="space-y-8 font-mono text-sm tracking-wide">
            <div className="border-l-2 border-border pl-6 py-1">
              <h3 className="text-muted-foreground uppercase text-xs mb-2 font-default">Communication</h3>
              <div className="flex items-center gap-3 text-foreground">
                <Mail className="size-4 text-primary" />
                <span>support@worldaccess.io</span>
              </div>
            </div>

            <div className="border-l-2 border-border pl-6 py-1">
              <h3 className="text-muted-foreground uppercase text-xs mb-2 font-default">Availability</h3>
              <div className="flex items-center gap-3 text-foreground">
                <Clock className="size-4 text-primary" />
                <span>24/7 Global Access</span>
              </div>
            </div>

            <div className="border-l-2 border-border pl-6 py-1">
              <h3 className="text-muted-foreground uppercase text-xs mb-2 font-default">Architecture</h3>
              <div className="flex items-center gap-3 text-foreground">
                <MapPin className="size-4 text-primary" />
                <span>Decentralized Infrastructure</span>
              </div>
            </div>
          </div>
        </div>

        {}
        <div className="bg-card/5 p-1 md:p-0">
          <FormProvider formKey="secure_inquiry_form_contact" formId="secure_inquiry_form_contact" sectionName="secure-inquiry-hub" defaultValues={{
          name: "",
          email: "",
          order_number: "",
          subject: "",
          message: "",
          attachment: undefined
        }} validationRules={{
          name: {
            required: true
          },
          email: {
            required: true,
            pattern: "email"
          },
          subject: {
            required: true
          },
          message: {
            required: true
          }
        }} submitText="INITIATE TRANSMISSION" successMessage="ENCRYPTED MESSAGE RECEIVED. STAND BY FOR RESPONSE." errorMessage="TRANSMISSION INTERRUPTED. PLEASE RETRY SESSION." className="w-full">
            <SupportForm />
          </FormProvider>
        </div>
      </div>
    </section>;
}
function SupportForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="h-full min-h-[400px] flex flex-col items-center justify-center text-center p-8 border border-border bg-white/5 backdrop-blur-md animate-in fade-in zoom-in duration-500 rounded-lg">
        <div className="size-16 bg-primary text-primary-foreground flex items-center justify-center rounded-full mb-6 shadow-[0_0_20px_rgba(0,255,255,0.3)]">
          <ArrowRight className="size-8" />
        </div>
        <h3 className="text-2xl font-default font-bold uppercase tracking-widest mb-2">Verified</h3>
        <p className="text-muted-foreground font-mono text-sm">{successMessage}</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="name" render={({
        field
      }) => <FormItem>
              <FormLabel className="sr-only">Your Name</FormLabel>
              <FormControl>
                <Input placeholder="IDENTIFIER / NAME" className="border-0 border-b border-border rounded-none bg-transparent px-0 py-4 h-auto focus-visible:ring-0 focus-visible:border-primary placeholder:text-muted-foreground/50 placeholder:uppercase placeholder:tracking-wider transition-colors font-mono" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" render={({
        field
      }) => <FormItem>
              <FormLabel className="sr-only">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="COMMUNICATION ADDRESS" className="border-0 border-b border-border rounded-none bg-transparent px-0 py-4 h-auto focus-visible:ring-0 focus-visible:border-primary placeholder:text-muted-foreground/50 placeholder:uppercase placeholder:tracking-wider transition-colors font-mono" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="order_number" render={({
        field
      }) => <FormItem>
              <FormLabel className="sr-only">Order Number</FormLabel>
              <FormControl>
                <Input placeholder="TXID / ORDER ID (OPTIONAL)" className="border-0 border-b border-border rounded-none bg-transparent px-0 py-4 h-auto focus-visible:ring-0 focus-visible:border-primary placeholder:text-muted-foreground/50 placeholder:uppercase placeholder:tracking-wider transition-colors font-mono" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="subject" render={({
        field
      }) => <FormItem>
              <FormLabel className="sr-only">Subject</FormLabel>
              <FormControl>
                <div className="relative">
                  <select className="w-full border-0 border-b border-border rounded-none bg-transparent px-0 py-4 h-auto focus:ring-0 focus:border-primary text-foreground placeholder:text-muted-foreground/50 uppercase tracking-wider text-sm appearance-none cursor-pointer outline-none transition-colors font-mono" {...field} value={field.value || ""}>
                    <option value="" disabled className="bg-card text-muted-foreground">
                      SELECT SUBJECT
                    </option>
                    <option value="General Inquiry" className="bg-card text-foreground">General Inquiry</option>
                    <option value="Order Issue" className="bg-card text-foreground">Service Access</option>
                    <option value="Product Question" className="bg-card text-foreground">Connectivity Issue</option>
                    <option value="Technical Issue" className="bg-card text-foreground">Payment Support</option>
                  </select>
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground">
                    <ArrowRight className="size-4 rotate-90" />
                  </div>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="sr-only">Message</FormLabel>
            <FormControl>
              <textarea placeholder="DESCRIBE YOUR REQUEST OR TECHNICAL ISSUE..." className="w-full min-h-[120px] border-0 border-b border-border rounded-none bg-transparent px-0 py-4 focus:ring-0 focus:border-primary placeholder:text-muted-foreground/50 placeholder:uppercase placeholder:tracking-wider resize-none outline-none transition-colors text-sm font-mono" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="attachment" render={({
      field: {
        value,
        onChange,
        ...field
      }
    }) => <FormItem>
            <FormLabel className="sr-only">Attachment</FormLabel>
            <FormControl>
              <div className="relative group cursor-pointer">
                <Input type="file" className="absolute inset-0 opacity-0 cursor-pointer z-10" onChange={e => {
            const file = e.target.files?.[0];
            if (file) onChange(file);
          }} {...field} />
                <div className="flex items-center gap-3 border-0 border-b border-border py-4 group-hover:border-primary transition-colors">
                  <Paperclip className="size-4 text-muted-foreground group-hover:text-primary transition-colors" />
                  <span className={`text-sm uppercase tracking-wider font-mono ${value ? 'text-primary' : 'text-muted-foreground/50'}`}>
                    {value ? (value as File).name : "UPLOAD PAYLOAD (OPTIONAL)"}
                  </span>
                </div>
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 text-destructive text-sm font-mono">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 font-default font-bold uppercase tracking-widest text-base transition-all hover:scale-[1.01] active:scale-[0.99] shadow-lg shadow-primary/20">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            TRANSMITTING...
          </> : "INITIATE TRANSMISSION"}
      </Button>
    </Form>;
}